/**
 * `useOnboardingExtractionJob` — async-aware extraction job hook for
 * the OnboardingWizardPage UI surface.
 *
 * Task 165: the deployed prod `extract-client-onboarding-from-website`
 * edge function (Task 093 internal) returns a `job_id` immediately and
 * runs the actual extraction in a background task that completes in
 * ~1–2 minutes. This hook encapsulates the four lifecycle phases:
 *
 *   1. `start(clientId)` — POST the start endpoint, branch on the
 *      response shape, and either short-circuit to success
 *      (`recently_succeeded` with a valid suggestion_id, or the
 *      legacy sync response with `suggestion_id` inline) or begin
 *      polling the job by `job_id`.
 *   2. polling — every 5 seconds, GET the status endpoint until the
 *      job reaches a terminal state.
 *   3. terminal — `succeeded` triggers `onSucceeded(suggestion_id,
 *      metadata)` (the wizard auto-applies); `failed` populates
 *      `errorMessage` for the UI retry button.
 *   4. timeout — after 3 minutes of polling without a terminal state,
 *      the hook gives up and surfaces a clean Arabic message.
 *
 * The hook handles three edge cases beyond the strategy-generation
 * reference (`useStrategyGenerationJob`):
 *   - `already_running`: the start endpoint returns an existing
 *     `job_id` when the user double-clicks or another tab/session is
 *     already extracting. The hook latches onto that job.
 *   - `recently_succeeded`: a previous run finished within the EF's
 *     dedup window. The hook treats that as immediate success.
 *   - Legacy sync EF: the master `extract-client-onboarding-from-website`
 *     EF still emits the synchronous response (`suggestion_id`
 *     inline, no `status` field). The hook detects this shape and
 *     applies immediately without polling. Keeps local dev working
 *     while the deployed prod EF stays async.
 *
 * The hook also recovers in-flight jobs on page load via
 * `recoverActiveJob(clientId)` — useful when the user reloads the
 * page mid-extraction. The polling loop attaches to whatever job is
 * currently `queued` or `running` for the client.
 *
 * Hard rules preserved from Task 164:
 *   - The user-facing error message NEVER contains the synthetic
 *     `KmiApiError` constructor string.
 *   - `apiError.code` + `apiError.body.fields` are logged to
 *     `console.error` for diagnosis, not surfaced to the UI.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isApiError } from '../../api/client';
import {
  extractClientOnboardingFromWebsite,
  getClientOnboardingExtractionStatus,
} from '../../api/endpoints';
import type {
  ExtractClientOnboardingFromWebsiteResponse,
  ExtractClientOnboardingFromWebsiteStartData,
  OnboardingExtractionJobRow,
  OnboardingExtractionJobStatus,
} from '../../api/types';

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 180_000;
const DEFAULT_ESTIMATED_SECONDS = 120;
const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const TERMINAL_STATUSES: ReadonlySet<OnboardingExtractionJobStatus> = new Set([
  'succeeded',
  'failed',
]);

export type OnboardingExtractionPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'starting' }
  | {
      readonly kind: 'tracking';
      readonly jobId: string;
      readonly status: OnboardingExtractionJobStatus;
      readonly startedAt: string;
      readonly estimatedSeconds: number;
      /**
       * One-shot "extraction was already running when you clicked"
       * info banner. The wizard renders this above the standard
       * tracking copy on the first tick after start; the hook flips
       * it false on the next phase transition so it doesn't linger.
       */
      readonly alreadyRunningNotice: boolean;
    }
  | {
      readonly kind: 'success';
      readonly jobId: string | null;
      readonly suggestionId: string;
    }
  | {
      readonly kind: 'failure';
      readonly jobId: string | null;
      readonly errorCode: string | null;
      readonly errorMessage: string;
    };

export interface UseOnboardingExtractionJobOptions {
  readonly clientId: string;
  /**
   * Called with the validated `suggestion_id` and (optionally) the
   * job row's `metadata` when the hook reaches a terminal `success`
   * phase. The wizard wires this to `applyClientOnboardingSuggestion`.
   */
  readonly onSucceeded: (suggestionId: string, metadata: Record<string, unknown> | null) => void;
}

export interface UseOnboardingExtractionJobResult {
  readonly phase: OnboardingExtractionPhase;
  readonly isBusy: boolean;
  readonly start: () => Promise<void>;
  readonly reset: () => void;
}

/**
 * Type guard: an async start response carries a `status` field whose
 * value is one of the start statuses. The legacy sync response has
 * no `status: 'started' | ...` — it has `status: 'pending_review' |
 * 'applied' | ...` (a different enum). Matching on the start enum
 * keeps the two shapes cleanly discriminated.
 */
function isAsyncStartResponse(
  res: ExtractClientOnboardingFromWebsiteResponse,
): res is ExtractClientOnboardingFromWebsiteStartData {
  if (typeof res !== 'object' || res === null) return false;
  const status = (res as { status?: unknown }).status;
  return status === 'started' || status === 'already_running' || status === 'recently_succeeded';
}

function isValidUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_REGEX.test(value);
}

export function useOnboardingExtractionJob(
  options: UseOnboardingExtractionJobOptions,
): UseOnboardingExtractionJobResult {
  const { clientId, onSucceeded } = options;
  const [phase, setPhase] = useState<OnboardingExtractionPhase>({ kind: 'idle' });

  // Latest onSucceeded in a ref so the polling loop doesn't restart
  // when the parent passes a fresh callback identity each render.
  const onSucceededRef = useRef(onSucceeded);
  onSucceededRef.current = onSucceeded;

  // Mutable refs so the polling effect can read fresh state without
  // taking a re-render dependency. Tracks the job_id we're polling
  // and the wall-clock start time for the timeout.
  const trackedJobIdRef = useRef<string | null>(null);
  const pollStartedAtRef = useRef<number | null>(null);

  // Recover an in-flight job on first mount / when clientId changes.
  // Best-effort: a 4xx (legacy EF that doesn't support GET) just
  // leaves the hook in `idle` until the user clicks.
  useEffect(() => {
    let cancelled = false;
    if (!clientId) return;
    void getClientOnboardingExtractionStatus({ client_id: clientId })
      .then((data) => {
        if (cancelled) return;
        const job = data.job;
        if (!job) return;
        if (job.status === 'queued' || job.status === 'running') {
          trackedJobIdRef.current = job.id;
          pollStartedAtRef.current = Date.now();
          setPhase({
            kind: 'tracking',
            jobId: job.id,
            status: job.status,
            startedAt: job.started_at ?? job.created_at,
            estimatedSeconds: data.estimated_seconds ?? DEFAULT_ESTIMATED_SECONDS,
            alreadyRunningNotice: false,
          });
        }
      })
      .catch(() => {
        // Best-effort recovery. Swallow.
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Polling loop. Only runs while phase === 'tracking'.
  useEffect(() => {
    if (phase.kind !== 'tracking') return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    // Codex Step A round 1 P1-A — every tick races the GET against
    // the remaining timeout budget via this AbortController. Without
    // this, a stalled GET that never resolves would block inside
    // `await getClientOnboardingExtractionStatus(...)` and the
    // top-of-tick timestamp check would never run again, holding the
    // hook past the 3-min cap.
    let abortController: AbortController | null = null;

    const fireTimeout = () => {
      trackedJobIdRef.current = null;
      pollStartedAtRef.current = null;
      setPhase({
        kind: 'failure',
        jobId: phase.jobId,
        errorCode: 'timeout',
        errorMessage: 'جاوز الاستخراج المهلة المسموحة. حاول مجدداً.',
      });
    };

    const tick = async () => {
      if (cancelled) return;
      const elapsed = pollStartedAtRef.current !== null ? Date.now() - pollStartedAtRef.current : 0;
      const remaining = POLL_TIMEOUT_MS - elapsed;
      // 3-minute hard timeout — surface a clean message and stop.
      if (remaining <= 0) {
        fireTimeout();
        return;
      }
      // Bound the GET to the remaining budget. If the request stalls
      // past `remaining` ms, abort + flip to timeout in the catch
      // branch below (the abort surfaces as an AbortError).
      abortController = new AbortController();
      const remainingTimer = setTimeout(() => {
        abortController?.abort();
      }, remaining);
      try {
        const data = await getClientOnboardingExtractionStatus({
          job_id: phase.jobId,
          signal: abortController.signal,
        });
        clearTimeout(remainingTimer);
        if (cancelled) return;
        const job = data.job;
        if (!job) {
          setPhase({
            kind: 'failure',
            jobId: phase.jobId,
            errorCode: 'not_found',
            errorMessage: 'تعذّر تتبّع حالة الاستخراج. حاول مجدداً.',
          });
          return;
        }
        if (TERMINAL_STATUSES.has(job.status)) {
          handleTerminalJob(job);
          return;
        }
        // Still in flight; update status (queued → running) and
        // schedule the next tick. Clear the one-shot
        // `alreadyRunningNotice` after the first follow-up tick so
        // it doesn't linger forever.
        setPhase((prev) =>
          prev.kind === 'tracking' && prev.jobId === phase.jobId
            ? {
                ...prev,
                status: job.status,
                startedAt: job.started_at ?? prev.startedAt,
                alreadyRunningNotice: false,
              }
            : prev,
        );
        timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        clearTimeout(remainingTimer);
        if (cancelled) return;
        // Codex Step A round 1 P1-A — distinguish "aborted because
        // we hit the 3-min budget mid-request" (terminate with
        // `timeout`) from a transient network failure (log +
        // reschedule).
        const isAbort = err instanceof DOMException && err.name === 'AbortError';
        if (isAbort && abortController?.signal.aborted) {
          fireTimeout();
          return;
        }
        // Transient network failure — log + keep polling. The
        // timeout above bounds the retry loop.
        if (isApiError(err)) {
          console.error('[onboarding-extract] poll tick failed', {
            code: err.code,
            status: err.status,
            fields: err.body.fields,
            job_id: phase.jobId,
          });
        } else {
          console.error('[onboarding-extract] poll tick failed (non-API error)', {
            job_id: phase.jobId,
          });
        }
        timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    const handleTerminalJob = (job: OnboardingExtractionJobRow) => {
      trackedJobIdRef.current = null;
      pollStartedAtRef.current = null;
      if (job.status === 'succeeded') {
        if (!isValidUuid(job.suggestion_id)) {
          console.error('[onboarding-extract] succeeded job missing valid suggestion_id', {
            job_id: job.id,
            suggestion_id: job.suggestion_id,
          });
          setPhase({
            kind: 'failure',
            jobId: job.id,
            errorCode: 'invalid_suggestion_id',
            errorMessage:
              'اكتمل الاستخراج لكن لم نحصل على معرّف صالح للنتائج. حاول مجدداً، وإن استمرت المشكلة عبّئ الحقول يدوياً.',
          });
          return;
        }
        setPhase({
          kind: 'success',
          jobId: job.id,
          suggestionId: job.suggestion_id,
        });
        onSucceededRef.current(job.suggestion_id, job.metadata ?? null);
      } else if (job.status === 'failed') {
        const errorCode = job.error_code;
        const errorMessage = mapJobErrorCodeToArabic(errorCode, job.error_message);
        console.error('[onboarding-extract] job failed', {
          job_id: job.id,
          error_code: errorCode,
          // error_message logged for diagnosis only; mapper produces
          // the user-facing copy.
          error_message: job.error_message,
        });
        setPhase({
          kind: 'failure',
          jobId: job.id,
          errorCode,
          errorMessage,
        });
      }
    };

    timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
      // Codex Step A round 1 P1-A — abort any in-flight GET so the
      // request doesn't keep a connection alive past unmount.
      abortController?.abort();
    };
  }, [phase]);

  const start = useCallback(async () => {
    if (!clientId) return;
    if (phase.kind === 'starting' || phase.kind === 'tracking') return;
    setPhase({ kind: 'starting' });
    try {
      const startRes = await extractClientOnboardingFromWebsite({ clientId });

      // ── Async-deployed EF branch ────────────────────────────────
      if (isAsyncStartResponse(startRes)) {
        if (startRes.status === 'recently_succeeded') {
          // Dedup short-circuit. The EF returned a recently terminal
          // job's suggestion_id directly. Apply without polling.
          if (!isValidUuid(startRes.suggestion_id)) {
            console.error(
              '[onboarding-extract] recently_succeeded response missing valid suggestion_id',
              { suggestion_id: startRes.suggestion_id, job_id: startRes.job_id },
            );
            setPhase({
              kind: 'failure',
              jobId: startRes.job_id,
              errorCode: 'invalid_suggestion_id',
              errorMessage:
                'تعذّر استخراج معرّف صالح للنتائج من الخدمة. حاول الاستخراج مجدداً، وإن استمرت المشكلة عبّئ الحقول يدوياً.',
            });
            return;
          }
          setPhase({
            kind: 'success',
            jobId: startRes.job_id,
            suggestionId: startRes.suggestion_id,
          });
          onSucceededRef.current(startRes.suggestion_id, null);
          return;
        }
        // `started` or `already_running` → enter polling.
        trackedJobIdRef.current = startRes.job_id;
        pollStartedAtRef.current = Date.now();
        setPhase({
          kind: 'tracking',
          jobId: startRes.job_id,
          status: startRes.status === 'already_running' ? 'running' : 'queued',
          startedAt: startRes.started_at,
          estimatedSeconds: startRes.estimated_seconds || DEFAULT_ESTIMATED_SECONDS,
          alreadyRunningNotice: startRes.status === 'already_running',
        });
        return;
      }

      // ── Legacy synchronous EF branch (master source) ────────────
      // The response shape matches `ExtractClientOnboardingFromWebsiteData`
      // with `suggestion_id` inline. Apply immediately, no polling.
      if (!isValidUuid(startRes.suggestion_id)) {
        console.error('[onboarding-extract] legacy sync response missing valid suggestion_id', {
          suggestion_id: startRes.suggestion_id,
        });
        setPhase({
          kind: 'failure',
          jobId: null,
          errorCode: 'invalid_suggestion_id',
          errorMessage:
            'تعذّر استخراج معرّف صالح للنتائج من الخدمة. حاول الاستخراج مجدداً، وإن استمرت المشكلة عبّئ الحقول يدوياً.',
        });
        return;
      }
      setPhase({ kind: 'success', jobId: null, suggestionId: startRes.suggestion_id });
      onSucceededRef.current(startRes.suggestion_id, null);
    } catch (err) {
      const errorMessage = mapStartErrorToArabic(err);
      const errorCode = isApiError(err) ? err.code : null;
      if (isApiError(err)) {
        console.error('[onboarding-extract] start failed', {
          code: err.code,
          status: err.status,
          fields: err.body.fields,
        });
      } else {
        console.error('[onboarding-extract] start failed (non-API error)');
      }
      setPhase({ kind: 'failure', jobId: null, errorCode, errorMessage });
    }
  }, [clientId, phase.kind]);

  const reset = useCallback(() => {
    trackedJobIdRef.current = null;
    pollStartedAtRef.current = null;
    setPhase({ kind: 'idle' });
  }, []);

  const isBusy = phase.kind === 'starting' || phase.kind === 'tracking';

  return { phase, isBusy, start, reset };
}

// ---------------------------------------------------------------------
// Internal: Arabic mappers + shape predicates. Exported for unit-test
// access only (the hook's stateful behavior is regression-pinned via
// source-grep — Task 164 ban on the synthetic `KmiApiError` constructor
// leak applies here too: mappers never echo `err.message` directly).
// ---------------------------------------------------------------------

export { isAsyncStartResponse as _isAsyncStartResponseForTests };
export { isValidUuid as _isValidUuidForTests };
export { mapStartErrorToArabic as _mapStartErrorToArabicForTests };
export { mapJobErrorCodeToArabic as _mapJobErrorCodeToArabicForTests };
export { POLL_INTERVAL_MS as _POLL_INTERVAL_MS_FOR_TESTS };
export { POLL_TIMEOUT_MS as _POLL_TIMEOUT_MS_FOR_TESTS };

function mapStartErrorToArabic(err: unknown): string {
  if (!isApiError(err)) return 'تعذّر بدء استخراج البيانات من الموقع. حاول مجدداً.';
  switch (err.code) {
    case 'permission_denied':
      return 'صلاحيتك لا تسمح بتشغيل الاستخراج. اطلب من مستخدم لديه صلاحية client.manage + ai.run.';
    case 'conflict':
      return 'يوجد استخراج قيد التنفيذ لهذا العميل. سيتم تحديث الحقول عند انتهائه.';
    case 'validation_error':
      return 'تعذّر بدء الاستخراج. تحقّق من رابط الموقع وحاول مجدداً.';
    case 'not_found':
      return 'العميل غير متاح أو تمت أرشفته.';
    case 'rate_limited':
      return 'الخدمة مشغولة حالياً. حاول بعد دقيقة.';
    case 'entitlement_denied':
      return 'الباقة الحالية لا تتضمن تشغيل النموذج.';
    case 'unauthenticated':
      return 'انتهت الجلسة. سيتم إعادة تسجيل الدخول…';
    case 'internal_error':
    default:
      return 'تعذّر بدء استخراج البيانات من الموقع. حاول مجدداً بعد قليل.';
  }
}

/**
 * Map the job row's `error_code` to a clean Arabic message. The raw
 * `error_message` from the EF is logged for diagnosis but never
 * surfaced — it can carry technical content (worker codes, fetch
 * failures, model errors) that the user shouldn't see.
 */
function mapJobErrorCodeToArabic(errorCode: string | null, _rawMessage: string | null): string {
  if (!errorCode) {
    return 'تعذّر إكمال الاستخراج. حاول مجدداً.';
  }
  switch (errorCode) {
    case 'unsafe_url':
    case 'robots_disallowed':
    case 'fetch_failed':
    case 'url_required':
      return 'تعذّر الوصول إلى موقع العلامة. تحقّق من رابط الموقع وحاول مجدداً.';
    case 'no_content':
    case 'empty_pages':
      return 'لم نجد محتوى كافياً في الموقع لاستخراجه. عبّئ الحقول يدوياً.';
    case 'model_failed':
    case 'worker_failed':
    case 'parse_failed':
      return 'تعذّر على النموذج فهم محتوى الموقع. حاول مجدداً أو عبّئ الحقول يدوياً.';
    case 'rate_limited':
      return 'الخدمة مشغولة حالياً. حاول بعد دقيقة.';
    case 'permission_denied':
      return 'صلاحيتك لا تسمح بتشغيل الاستخراج.';
    default:
      return 'تعذّر إكمال الاستخراج. حاول مجدداً.';
  }
}
